<template>
    <div>
        <p v-if="!invoiceList.length" class="text-center mt-5">No invoices found for this order.</p>

        <div v-for="invoice in invoiceList" :key="invoice.id" class="card general-card invoice-card">
            <div class="invoice-card-header">
                <div class="row align-items-center">
                    <div class="col-md-4 col-12">
                        <p class="mb-25 invoice-title">
                            <i class="fas fa-file-invoice me-25"></i> {{ invoice.bill_number }}
                        </p>
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">{{ readableDate(invoice.date) }}</p>
                    </div>
                    <div class="col-md-4 col-12">
                        <p class="mb-0">Customer: {{ invoice.contact_name }}</p>
                    </div>
                    <div class="col-md-4 col-12 d-flex justify-content-end align-items-center">
                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="onClickPrint(invoice)">
                            <i class="fas fa-print"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="px-1 pb-1 pt-1">
                <div class="row align-items-center">
                    <div class="col-md-3 col-6">
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">Subtotal</p>
                        <p class="mb-0">{{ commaFormat(invoice.subtotal_amount) }}</p>
                    </div>
                    <div class="col-md-3 col-6">
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">VAT</p>
                        <p class="mb-0">{{ commaFormat(invoice.vat_amount) }}</p>
                    </div>
                    <div class="col-md-3 col-6">
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">Total Amount</p>
                        <p class="mb-0 amount-value">{{ commaFormat(invoice.total_amount) }}</p>
                    </div>
                    <div class="col-md-3 col-6">
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">Paid Amount</p>
                        <p class="mb-0">{{ commaFormat(invoice.paid_amount) }}</p>
                    </div>
                </div>

                <div v-if="invoice.description" class="row align-items-center mt-25">
                    <div class="col-12">
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">Note</p>
                        <p class="mb-0">{{ invoice.description }}</p>
                    </div>
                </div>

                <div v-if="generalsOf(invoice).length" class="table-responsive mt-25">
                    <table class="table table-bordered custom-table-padding mb-0">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th class="text-right">Quantity</th>
                                <th class="text-right">Rate</th>
                                <th class="text-right">VAT</th>
                                <th class="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in generalsOf(invoice)" :key="row.id">
                                <td>{{ row.product_name }}</td>
                                <td class="text-right">{{ row.quantity }}</td>
                                <td class="text-right">{{ commaFormat(row.rate) }}</td>
                                <td class="text-right">{{ commaFormat(row.vat_amount) }}</td>
                                <td class="text-right">{{ commaFormat(row.total_amount) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <Loader v-if="printing"/>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import figureFormatter from '@/services/utils/figureFormatter'
import useDate from '@/services/utils/day'
import handlePurchase from '@/services/modules/purchase'
import handleBarcode from '@/services/modules/barcode'
import handleCompany from '@/services/modules/company'
import pdfPrinter from '@/services/utils/pdfPrinter'
import Loader from '@/components/atom/LoaderComponent'

const props = defineProps({
    orderDetail: { type: Object, required: true }
})

const route = useRoute()
const store = useStore()
const { commaFormat } = figureFormatter()
const { formatToDdMmYy, formatDateTime } = useDate()
const { fetchSalesBill } = handlePurchase()
const { fetchBarcode } = handleBarcode()
const { fetchCompanyInfo } = handleCompany()
const { exportToPDF: exportSaleInvoicePDF } = pdfPrinter()

const printing = ref(false)
const companyInfo = ref({})

const invoiceList = computed(() => {
    if (!props.orderDetail.sale_invoices) return []
    return props.orderDetail.sale_invoices
})

const generalsOf = (invoice) => {
    if (!invoice.general) return []
    return invoice.general
}

const readableDate = (date) => {
    if (!date) return ''
    return formatToDdMmYy(String(date).split(' ')[0])
}

const companyQuery = () => {
    return `?company_id=${route.params.companyId}`
}

const buildQrCodeQuery = (billNumber, refDate, userName) => {
    const qrcodeData = JSON.stringify({
        ref_no: billNumber,
        ref_date: refDate,
        created_by: userName,
        created_date_time: formatDateTime(new Date())
    })
    return `?code=${billNumber}&qrcode=${qrcodeData}`
}

const loadCompanyInfo = async () => {
    if (companyInfo.value.id) return
    await fetchCompanyInfo(route.params.companyId).then((res) => {
        companyInfo.value = res.data
    })
}

const onClickPrint = async (invoice) => {
    if (!invoice.id) return
    const user = store.state.user.user
    printing.value = true
    await loadCompanyInfo()

    await fetchSalesBill(invoice.id, companyQuery()).then(async (res) => {
        if (!res.data) return
        const data = res.data
        const barcodeQuery = buildQrCodeQuery(data.bill_number, data.date, data.created_by_name)
        await fetchBarcode(barcodeQuery).then((res) => {
            if (!res.status) return
            exportSaleInvoicePDF(companyInfo.value, data, res.barcode, res.qrcode, user.name, 1)
        })
    })

    printing.value = false
}
</script>

<style scoped>
.general-card {
    min-height: auto;
}
.invoice-card {
    overflow: hidden;
    margin-bottom: 1rem;
}
.invoice-card-header {
    padding: 0.85rem 1rem;
    background-color: rgba(115, 103, 240, 0.06);
    border-bottom: 1px solid rgba(115, 103, 240, 0.14);
}
.invoice-title {
    font-size: 15px;
    font-weight: 600;
}
.amount-value {
    font-size: 15px;
    font-weight: 700;
    color: #7367f0;
}
.fw-bold {
    font-weight: bold !important;
}
.custom-table-padding :deep(td),
.custom-table-padding :deep(th) {
    padding: 6px 10px;
    font-size: 13px;
}
</style>
